import * as yup from 'yup';
import { useExperiment } from '@web/experiments';
import {
  nullableImageFromCMSSchema,
  requiredImageFromCMSSchema,
  richTextFromCMSSchema,
} from '../../cms';

const useExperimentValues = value => {
  let { value: experimentValue } = useExperiment(
    value.question[0].test_name ?? null,
  );
  experimentValue = experimentValue || 'control';
  let questionVariants = value.question[0];
  const experimentVariation = questionVariants?.variants.find(
    variant => variant.variant_name === experimentValue,
  );

  questionVariants = experimentVariation?.blocks[0] ?? [];
  return questionVariants;
};

const itemSchema = yup.object({
  title: yup.string().required(),
  label: yup.string().required(),
  benefit: yup.string().optional(),
  callout: yup.string().optional(),
  value: yup.string().required(),
  description: richTextFromCMSSchema,
  img: requiredImageFromCMSSchema,
  unavailableText: yup.string(),
  priceSymbol: yup.string().optional(),
  showMoreDetails: yup.bool(),
  pricingInfo: yup.object({
    price: yup.string(),
    discount: yup.string(),
    period: yup.string(),
  }),
  level: yup.string(),
  hidden: yup.bool(),
});

export const recommendedPlanSchema = yup
  .object({
    ollieLogo: nullableImageFromCMSSchema,
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    images: yup.array(requiredImageFromCMSSchema),
    slideDuration: yup.number(),
    captions: yup.array(),
    crunchingNumbersHeadline: yup.string(),
    ctaText: yup.string(),
    body: yup.array(),
    subheadline: yup.string(),
    carousel: yup.array(nullableImageFromCMSSchema),
    mealPrepTypes: yup
      .array(
        itemSchema
          .from('display_name', 'title')
          .from('slug', 'value')
          .from('image', 'img')
          .from('unavailable_text', 'unavailableText')
          .from('show_more_details', 'showMoreDetails')
          .from('hidden', 'hidden'),
      )
      .required(),
    collapsedMealPrepTypes: yup
      .array(
        itemSchema
          .from('display_name', 'title')
          .from('slug', 'value')
          .from('image', 'img')
          .from('unavailable_text', 'unavailableText')
          .from('show_more_details', 'showMoreDetails')
          .from('hidden', 'hidden'),
      )
      .required(),
    mealPlanModalContent: yup.array(
      itemSchema
        .from('display_name', 'title')
        .from('slug', 'value')
        .from('image', 'img')
        .from('unavailable_text', 'unavailableText')
        .from('show_more_details', 'showMoreDetails')
        .from('hidden', 'hidden'),
    ),
  })
  .transform(value => {
    let question = null;
    if (value.question[0].component === 'flagship_ab_test') {
      question = useExperimentValues(value);
    } else {
      question = value.question[0];
    }
    return {
      ollieLogo: value.ollieLogo,
      mobileImage: question.mobileImage,
      desktopImage: question.desktopImage,
      images: question.crunchingNumbersContent[0].images,
      captions: question.crunchingNumbersContent[0].captions.split('/'),
      slideDuration: Number(question.crunchingNumbersContent[0].slideDuration),
      crunchingNumbersHeadline: question.crunchingNumbersContent[0].headline,
      backgroundColor:
        question.crunchingNumbersContent[0].backgroundColor || undefined,
      fontColor: question.crunchingNumbersContent[0].fontColor || undefined,
      headline: question.headline || 'Recommended for {{pupName}}',
      subheadline: question.subheadline,
      carousel: question.carousel,
      mealPrepTypes: question.mealPlanTypes
        .filter(item => !item.hidden)
        .map(recipe => ({
          ...recipe,
          priceSymbol: recipe.price_symbol,
          pricingInfo: {
            price: recipe.price,
            discount: recipe.discount,
          },
        })),
      collapsedMealPrepTypes: question.collapsedMealPlanTypes
        .filter(item => !item.hidden)
        .map(recipe => ({
          ...recipe,
          priceSymbol: recipe.price_symbol,
          pricingInfo: {
            price: recipe.price,
            discount: recipe.discount,
          },
        })),
      body: question.body,
      ctaText: value.ctaButtonText || 'Continue',
      mealPlanModalContent: question.modalContent
        .filter(item => !item.hidden)
        .map(recipe => ({
          ...recipe,
          priceSymbol: recipe.price_symbol,
          pricingInfo: {
            price: recipe.price,
            discount: recipe.discount,
            period: recipe.period || 'week',
          },
        })),
    };
  });
